import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, ConfirmationComponent, Integration, IntegrationHealth, IntegrationService, IntegrationTypeEnum, OpenAIIntegration, Pager } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'app-agent-openai',
    templateUrl: './agent-openai.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentOpenAIComponent extends AbstractComponent {
  pager: Pager<Integration> = new Pager<Integration>({ perPage: 10 });
  listObservable: Observable<Pager<Integration>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private integrationService: IntegrationService
  ) {
    super(injector);
    this.loadingContent = true;
  }

  ngOnInit(): void {
    this.getList();
  }

  getList() {
    this.loadingContent = true;

    this.searchParams = {
      company: this.getIDCurrentCompany(),
      type: IntegrationTypeEnum.OPENAI,
    };

    this.integrationService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Integration>) => {
          this.pager = pager;
          this.setupObservableSearch();
        },
        complete: () => (this.loadingContent = false),
      });
  }

  setupObservableSearch() {
    this.listObservable = this.termOfSearch.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        return this.integrationService.getAll(
          this.pager.page,
          this.pager.perPage,
          term,
          this.searchParams
        );
      })
    );

    this.listObservable.subscribe({
      next: (pager: Pager<Integration>) => {
        this.pager = pager;
      }
    });
  }

  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  createIntegration() {
    this.router.navigate(['/agents/openai/integration/create']);
  }

  edit(integration: Integration) {
    this.router.navigate([`/agents/openai/integration/${integration._id}/edit`]);
  }

  openAssistants(integration: Integration) {
    this.router.navigate([`/agents/openai/${integration._id}/assistants`]);
  }

  /** Circuit-breaker state written by the backend when OpenAI refused the integration. */
  health(integration: Integration): IntegrationHealth | undefined {
    return (<OpenAIIntegration>integration?.metadata)?.health;
  }

  healthLabelKey(integration: Integration): string {
    return `agents.openai.health.${this.health(integration)?.status ?? 'GENERIC'}`;
  }

  reactivate(integration: Integration) {
    this.integrationService.resetHealth(integration._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.getList();
          this.alertService.success(this.t('agents.openai.reactivateSuccess'));
        },
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('agents.openai.reactivateError')),
      });
  }

  delete(integration: Integration) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('agents.openai.deleteConfirm', { name: integration.name })
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.integrationService.delete(integration._id).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(this.t('agents.openai.deleteSuccess'));
          },
          error: (err) => this.alertService.error(err.error.message),
        });
      }
    });
  }
}